import React, { Component } from 'react';
import CaseCloseHeaderComponent from './CaseCloseHeaderComponent';
import { Dialog } from 'primereact/dialog';
import CaseCloseReasonComponent from './CaseCloseReasonComponent';
import { AlarmCloseCode, CaseSettings } from '../../../../domain/case/CaseDomains';
import { TransactionData, TransactionReason } from '../../../../domain/stream/transaction/TransactionDomains';
import CaseCloseListComponent from './CaseCloseListComponent';
import CaseActionModalComponent from '../action/CaseActionModalComponent';
import CaseService from '../../../../service/CaseService';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import MessageUtil from '../../../../util/MessageUtil';
import LanguageUtil from '../../../../util/LanguageUtil';
import { RootState } from '../../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import AnimatedButton from 'components/custom/AnimatedButton';
import ComponentName from 'constant/ComponentName';

class CaseCloseContainer extends Component<Props, State> {
  public state: State = new State();

  private readonly alarmService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);

  public render() {
    const { selectedTab } = this.state;
    const { visible, type, onHide } = this.props;
    return (
      <Dialog
        visible={visible}
        onHide={() => {
          this.setState({ reason: undefined });
          onHide();
        }}
        header={<CaseCloseHeaderComponent selectedTab={selectedTab} type={type} onSelectTab={this.onSelectTab} />}
        footer={this.getButtons()}
        style={{ minHeight: '376px' }}
        headerStyle={{ borderBottom: '1px solid #E0E4E8', background: '#F9FAFB', padding: 0, boxShadow: 'none' }}
        contentStyle={{ padding: '0px', height: '100%', display: 'flex' }}
      >
        <div className='flex w-[1000px] min-h-full h-full items-start justify-center'>{this.getStep()}</div>
      </Dialog>
    );
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { type } = this.props;
    if (prevProps.type != this.props.type) {
      this.setState({ selectedTab: 'reason', reason: undefined, reasonRequiredCheck: false });
    }
  }

  private onSelectTab = (key: string) => {
    this.setState({ selectedTab: key });
  };

  private getCloseCodes = (): AlarmCloseCode[] => {
    const { settings, type, user } = this.props;
    const riskType = user.activeRiskType;
    if (type == 'ON_HOLD') {
      return settings.closeCodes.filter((closeCode) => closeCode.status == 'HOLD');
    }
    const closeType = type == 'FRAUD' ? (riskType == 'fraud' ? 'FRAUD' : 'IN_COMPLIANT') : riskType == 'fraud' ? 'NOT_FRAUD' : 'COMPLIANT';
    return settings.closeCodes.filter((closeCode) => closeCode.status == closeType);
  };

  private getStep = () => {
    const { selectedTab, reason } = this.state;
    const { settings, type, transaction, user } = this.props;
    const closeCodes = this.getCloseCodes();
    switch (selectedTab) {
      case 'reason':
        return <CaseCloseReasonComponent value={reason} reasons={closeCodes} onChange={this.onChangeReasonFromComponent} settings={settings} />;
      case 'list':
        return <CaseCloseListComponent domain={settings} transactionData={transaction} />;
      case 'action':
        return (
          <CaseActionModalComponent
            actions={settings.actions}
            transactionData={transaction}
            reason={reason}
            riskType={user.activeRiskType}
            onChangeActionRequiredCheck={this.onChangeActionRequiredCheck}
            onChangeActionErrorCheck={this.onChangeActionErrorCheck}
            closeType={type}
          />
        );
    }
  };

  private getButtons = () => {
    const { actionRequiredCheck, actionErrorCheck, reasonRequiredCheck } = this.state;
    const { settings, onHide, type } = this.props;
    const submitLabel =
      type == 'ON_HOLD'
        ? LanguageUtil.get({ id: 'common.label.hold', defaultMessage: 'Hold' })
        : LanguageUtil.get({ id: 'common.label.close', defaultMessage: 'Close' });
    const isHold = type === 'ON_HOLD';
    const isActionRequired = !isHold && settings?.actionRequired !== undefined && settings.actionRequired;
    const isReasonRequired = settings?.reasonRequired === true;

    return (
      <div className='flex w-full gap-x-5 px-5 py-5 items-center justify-end gap-[10px]'>
        <AnimatedButton
          className={'!min-w-[120px]'}
          customType='secondary'
          text={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
          icon={'arrow-left'}
          onClick={onHide}
          data-testid={`${ComponentName.CASE}_close_back_button`}
        />
        <AnimatedButton
          className={'!min-w-[120px]'}
          customType='primary'
          text={submitLabel}
          icon={'arrow-right'}
          disabled={(isActionRequired && (!actionRequiredCheck || actionErrorCheck)) || (isReasonRequired && !reasonRequiredCheck)}
          onClick={this.close}
          data-testid={`${ComponentName.CASE}_close_next_button`}
        />
      </div>
    );
  };

  private closeCase = async () => {
    const { router, type, user, onLocalCaseMutation, onLocalCaseMutationFailed } = this.props;
    const previousSearchParams = new URLSearchParams(router.params.id);
    const domainName: string | null = previousSearchParams.get('domain');
    const index: string | null = previousSearchParams.get('index');
    const riskType = user.activeRiskType;
    const _type =
      type == 'FRAUD'
        ? riskType == 'fraud'
          ? TransactionReason.FRAUD
          : TransactionReason.IN_COMPLIANT
        : riskType == 'fraud'
          ? TransactionReason.NOT_FRAUD
          : TransactionReason.COMPLIANT;
    if (domainName && index) {
      onLocalCaseMutation(_type, this.state.reason ?? '');
      await this.alarmService
        .closeCase(domainName, index, _type, this.state.reason)
        .then(() => {
          MessageUtil.success({
            summary: LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' }),
            detail: <div>{LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' })} closed</div>,
          });
        })
        .catch(() => {
          onLocalCaseMutationFailed();
          MessageUtil.error({
            summary: LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' }),
            detail: <div>{LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' })} close failed</div>,
          });
        });
    }
  };

  private onHoldCase = async () => {
    const { router } = this.props;
    const previousSearchParams = new URLSearchParams(router.params.id);
    const domainName: string | null = previousSearchParams.get('domain');
    const index: string | null = previousSearchParams.get('index');
    if (domainName && index) {
      this.props.onLocalCaseMutation(TransactionReason.ON_HOLD, this.state.reason ?? '');
      await this.alarmService
        .holdCase(domainName, index, this.state.reason)
        .then(() => {
          MessageUtil.success({
            summary: LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' }),
            detail: <div>{LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' })} is on hold</div>,
          });
        })
        .catch(() => {
          this.props.onLocalCaseMutationFailed();
          MessageUtil.error({
            summary: LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' }),
            detail: <div>{LanguageUtil.get({ id: `common.label.case`, defaultMessage: 'Case' })} hold failed</div>,
          });
        });
    }
  };

  private onChangeActionRequiredCheck = (value: boolean) => {
    this.setState({ actionRequiredCheck: value });
  };

  private onChangeActionErrorCheck = (value: boolean) => {
    this.setState({ actionErrorCheck: value });
  };

  private onChangeReason = (reason: string) => {
    this.setState({ reason, reasonRequiredCheck: !!reason });
  };

  private onChangeReasonFromComponent = (reason: string) => {
    this.setState({ reason, reasonRequiredCheck: !!reason });
  };

  private close = async () => {
    const { actionRequiredCheck, actionErrorCheck, reasonRequiredCheck } = this.state;
    const { settings, type } = this.props;
    const isActionRequired = settings?.actionRequired !== undefined && settings.actionRequired;
    const isReasonRequired = settings?.reasonRequired === true;
    if (type == 'ON_HOLD') {
      if (isReasonRequired && !reasonRequiredCheck) return;
      await this.onHoldCase();
    } else {
      if (isActionRequired && (!actionRequiredCheck || actionErrorCheck)) return;
      if (isReasonRequired && !reasonRequiredCheck) return;
      await this.closeCase();
    }
    this.props.onHide();
  };
}

interface Props extends FormicaProps, ReduxProps {
  settings: CaseSettings;
  type: 'FRAUD' | 'NOT_FRAUD' | 'ON_HOLD';
  transaction: TransactionData;
  visible: boolean;
  onHide: () => void;
  onLocalCaseMutation: (status: TransactionReason, code: string) => void;
  onLocalCaseMutationFailed: () => void;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  selectedTab: string = 'reason';
  reason: string | undefined;
  loading: boolean = true;
  actionRequiredCheck: boolean = false;
  actionErrorCheck: boolean = false;
  reasonRequiredCheck: boolean = false;
}

export default withFormica(connector(CaseCloseContainer));
